import { useState, useContext, useMemo } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormDate,
    SttFormText,
    SttLabelForm,
    SttSelectMship,
} from '../../../sporttia/all';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type User = {
    id?: number;
    name?: string;
};

type Mship = {
    id: number;
    fullName: string;
    user?: User;
};

type UserRestriction = {
    id?: string;
    created?: string;
    user?: User;
    mship?: Mship;
    ini?: string;
    end?: string;
};

type Props = {
    item: UserRestriction;
    onSave: (userRestriction: UserRestriction) => void;
    onDelete: (userRestriction: UserRestriction) => void;
    onClose: () => void;
};

export default function RestrictionUserDialog({
    item,
    onSave,
    onDelete,
    onClose,
}: Props): React.ReactElement {
    const [userRestriction, setUserRestriction] =
        useState<UserRestriction>(item);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // Derive mship from user for display in selector
    const currentMship = useMemo((): Mship | null => {
        if (userRestriction.mship) {
            return userRestriction.mship;
        }
        if (userRestriction.user?.id) {
            return {
                id: userRestriction.user.id,
                fullName: userRestriction.user.name || '',
                user: userRestriction.user,
            };
        }
        return null;
    }, [userRestriction.mship, userRestriction.user]);

    const handleMshipSelect = (mship: Mship | null) => {
        if (mship) {
            setUserRestriction({
                ...userRestriction,
                mship,
                user: mship.user || {
                    id: mship.id,
                    name: mship.fullName,
                },
            });
        } else {
            setUserRestriction({
                ...userRestriction,
                mship: undefined,
                user: undefined,
            });
        }
    };

    return (
        <SttDialog
            maxWidth="sm"
            open={userRestriction !== undefined}
            title={t('RestrictionUser')}
            onClose={onClose}
            content={
                <Grid container spacing={3}>
                    {userRestriction.created && (
                        <Grid size={{ xs: 12 }}>
                            <SttLabelForm
                                title={t('Created')}
                                text={new DateBuilder(
                                    userRestriction.created,
                                    cxt?.sc?.timezone?.name,
                                    cxt?.lang?.key,
                                ).dmy()}
                            />
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        {!userRestriction.id ? (
                            <SttSelectMship
                                caption={t('User')}
                                mship={currentMship}
                                onSelect={handleMshipSelect}
                            />
                        ) : (
                            <SttFormText
                                disabled
                                caption={t('User')}
                                defVal={userRestriction.user?.name || ''}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SttFormDate
                            caption={t('ValidFrom')}
                            name="ini"
                            defVal={new DateBuilder(userRestriction.ini).ymd()}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setUserRestriction({
                                    ...userRestriction,
                                    ini: value as string,
                                });
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <SttFormDate
                            caption={t('until')}
                            name="end"
                            defVal={new DateBuilder(userRestriction.end).ymd()}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setUserRestriction({
                                    ...userRestriction,
                                    end: value as string,
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            }
            buttons={
                [
                    userRestriction.id
                        ? {
                              type: 'delete',
                              onClick: () => onDelete(userRestriction),
                              permission: PERMISSIONS.RESTRICTION.MODIFY,
                          }
                        : undefined,
                    {
                        type: 'save',
                        onClick: () => onSave(userRestriction),
                        permission: PERMISSIONS.RESTRICTION.MODIFY,
                    },
                ] as any
            }
        />
    );
}
